const SsoUtils = require('utils/sso');
const Storage = require('utils/storage');
const PersistenceConfig = require('utils/persistence-config');

const E2E_PROVIDER_BEARER = 'e2e-sso-provider-token';

const internals = {};

exports.isEnabled = () => {

    if (typeof window === 'undefined') {
        return false;
    }

    if (process.env.NODE_ENV === 'production') {
        return false;
    }

    return Storage.getItem(PersistenceConfig.keys.e2e.mockSsoProvider) === '1';
};

exports.createClient = ({
    email,
    onRedirectRequestCreated,
    provider,
    redirectUri,
    setAuthCode,
    setAuthState,
    sso
}) => {

    let mockAuthCode = null;
    let mockAuthState = null;
    let mockTokenResponse = null;

    return {
        async loginWithRedirect({ authorizationParams }) {
            if (window.__E2E_SSO_REDIRECT_STARTED__) {
                return undefined;
            }

            window.__E2E_SSO_REDIRECT_STARTED__ = true;

            const state = `e2e-sso-state-${Date.now()}`;
            const code = `e2e-sso-code-${Date.now()}`;

            mockAuthCode = code;
            mockAuthState = state;

            onRedirectRequestCreated?.({
                state,
                codeVerifier: 'e2e-sso-code-verifier',
                redirectUri: authorizationParams?.redirect_uri
            });

            setAuthState?.(state);
            setAuthCode?.(code);
            return undefined;
        },

        async handleRedirectCallback() {
            mockTokenResponse = await internals.exchangeProviderToken({
                code: mockAuthCode,
                email,
                provider,
                redirectUri,
                sso,
                state: mockAuthState
            });

            return mockTokenResponse;
        },

        async getIdTokenClaims() {
            if (!mockTokenResponse?.id_token) {
                return null;
            }

            return {
                ...SsoUtils.decodeJwt(mockTokenResponse.id_token),
                __raw: mockTokenResponse.id_token
            };
        },

        async clearSession() {
            mockTokenResponse = null;
            mockAuthCode = null;
            mockAuthState = null;
            return undefined;
        },

        async logout() {
            mockTokenResponse = null;
            mockAuthCode = null;
            mockAuthState = null;
            return undefined;
        }
    };
};

internals.exchangeProviderToken = async ({
    code,
    email,
    provider,
    redirectUri,
    sso,
    state
}) => {

    if (!code || !state) {
        throw new Error('Missing e2e SSO code/state');
    }

    if (!email) {
        throw new Error('Missing e2e SSO email');
    }

    const parsed = SsoUtils.parseSSO(sso);
    const connection = parsed.loginMethods;

    if (!connection) {
        throw new Error('Missing e2e SSO connection');
    }

    const customNonce = `e2e-sso-nonce-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    await internals.registerToken({
        connection,
        customNonce,
        email,
        provider
    });

    const nowSeconds = Math.floor(Date.now() / 1000);
    const idToken = internals.createJwt({
        aud: 'nearpeer-e2e',
        code,
        custom_nonce: customNonce,
        email,
        exp: nowSeconds + 300,
        iat: nowSeconds,
        iss: `nearpeer-e2e-${provider}`,
        nonce: state,
        preferred_username: email,
        redirect_uri: redirectUri,
        sub: `${provider}|${connection}|${email}`
    });

    return {
        id_token: idToken,
        access_token: 'e2e-sso-access-token',
        token_type: 'Bearer',
        expires_in: 300
    };
};

internals.registerToken = async ({
    connection,
    customNonce,
    email,
    provider
}) => {

    const apiBaseUrl = internals.getApiBaseUrl();
    const route = provider === 'keycloak'
        ? '/keycloak-register-token'
        : '/auth0-register-token';

    const response = await fetch(`${apiBaseUrl}${route}`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${E2E_PROVIDER_BEARER}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(internals.getRegisterTokenPayload({
            connection,
            customNonce,
            email,
            provider
        }))
    });

    if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`E2E SSO register-token failed: ${response.status} ${errorBody}`);
    }
};

internals.getRegisterTokenPayload = ({
    connection,
    customNonce,
    email,
    provider
}) => {

    if (provider === 'keycloak') {
        return {
            custom_nonce: customNonce,
            connection,
            user: {
                keycloakUserId: `e2e-keycloak|${email}`,
                externalId: `e2e-keycloak|${email}`,
                email,
                firstName: 'E2E',
                lastName: 'SSO'
            }
        };
    }

    return {
        custom_nonce: customNonce,
        user: {
            user_id: `samlp|${connection}|${email}`,
            email,
            emailaddress: email,
            objectidentifier: `e2e-auth0|${email}`,
            identities: [{
                connection,
                userId: `${connection}|${email}`
            }],
            nameIdAttributes: {
                value: email
            }
        }
    };
};

internals.getApiBaseUrl = () => {

    const host = process.env.API_HOST || window.location.origin;
    const prefix = process.env.API_PREFIX || '/api';

    return `${String(host).replace(/\/$/, '')}${prefix}`;
};

internals.createJwt = (payload) => {

    const header = {
        alg: 'HS256',
        typ: 'JWT'
    };

    return [
        internals.base64UrlEncode(JSON.stringify(header)),
        internals.base64UrlEncode(JSON.stringify(payload)),
        'e2e-signature'
    ].join('.');
};

internals.base64UrlEncode = (value) => window.btoa(value)
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

exports.internals = internals;
